import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilValue } from "recoil";
import { useGetOfficeApplianceType } from "../hooks/useGetOfficeApplianceType";
import { useGetOfficeApplianceBrand } from "../hooks/useGetOfficeApplianceBrand";
import { useGetRetailStoreOptions } from "../hooks/useGetRetailStoreOptions";
import SelectHookFormSingle from "components/SelectHookFormSingle";
import TextInputHookFormDisabled from "components/TextInputHookFormDisabled";
import TextInputLabelHookForm from "components/TextInputLabelHookForm";
import NumericInputLabel from "components/NumericInputLabel";
import CheckBoxHookForm_2 from "components/CheckBoxHookForm_2";
import { applianceOfficeId, showApplianceFromOffice } from "../atom";
import { useEffect } from "react";

const OfficeApplianceFormDetails = ({ isEditState }) => {
  //#region Data
  const { data: officeApplianceType, isLoading: typeLoading } =
    useGetOfficeApplianceType();
  const { data: officeApplianceBrand, isLoading: brandLoading } =
    useGetOfficeApplianceBrand();
  const { data: retailStores, isLoading: storeLoading } =
    useGetRetailStoreOptions();
  //#endregion

  //#region States
  const officeId = useRecoilValue(applianceOfficeId);
  const showFromOffice = useRecoilValue(showApplianceFromOffice);
  //#endregion

  //#region FormContext
  const {
    register,
    formState: { errors },
    control,
    setValue,
  } = useFormContext();
  //#endregion

  //#region Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const deletePermission = PERMISSIONS.permission_officeappliance_delete;
  //#endregion

  //#region Dropdown Options
  const typeOpts = officeApplianceType?.map((x) => {
    return {
      label: x.officeApplianceType1,
      value: x.officeApplianceTypeId,
    };
  });

  const brandOpts = officeApplianceBrand?.map((x) => {
    return {
      label: x.officeApplianceBrand1,
      value: x.officeApplianceBrandId,
    };
  });

  const retailStoreOpts = retailStores?.map((x) => {
    return {
      label: x.retailStoreNumber,
      value: x.officeId,
      address: x.retailStoreAddress,
    };
  });
  //#endregion

  //#region From Office
  useEffect(() => {
    if (officeId) {
      const address = retailStores?.find((x) => x.officeId === officeId);

      setValue("officeId", officeId);
      setValue("retailStoreAddress", address?.retailStoreAddress);
    }
  }, [officeId, retailStores, setValue]);
  //#endregion

  return (
    <div className="bg-bgDB space-y-2">
      <div className="bg-white box-shadow rounded-lg h-full">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Office Appliance Details
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 mx-4 pb-2 gap-2 gap-x-2  ">
          <div className="space-y-1 mr-6">
            <Controller
              name="officeId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => {
                    field.onChange(e ? e.value : null);
                    setValue("retailStoreAddress", e.address);
                  }}
                  value={field.value}
                  options={retailStoreOpts}
                  isLoading={storeLoading}
                  label="Retail Store Number"
                  className={
                    errors.officeId && "border-2 border-red-500 col-span-2 "
                  }
                  required={true}
                  isDisabled={showFromOffice}
                />
              )}
            />
            <TextInputHookFormDisabled
              name="retailStoreAddress"
              label="Retail Store Address"
              type="text"
              register={register("retailStoreAddress", {})}
            />
            <Controller
              name="officeApplianceTypeId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => {
                    field.onChange(e ? e.value : null);
                  }}
                  value={field.value}
                  options={typeOpts}
                  isLoading={typeLoading}
                  label="Type"
                  className={
                    errors.officeApplianceTypeId &&
                    "border-2 border-red-500 col-span-2 "
                  }
                  required={true}
                />
              )}
            />
            <Controller
              name="officeApplianceBrandId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => {
                    field.onChange(e ? e.value : null);
                  }}
                  value={field.value}
                  options={brandOpts}
                  isLoading={brandLoading}
                  label="Brand"
                  className={
                    errors.officeApplianceBrandId &&
                    "border-2 border-red-500 col-span-2 "
                  }
                  required={true}
                />
              )}
            />
            <TextInputLabelHookForm
              name="purchaseDate"
              label="Purchase Date"
              type="date"
              required={true}
              className={
                errors.purchaseDate && "border-2 border-red-500 col-span-2 "
              }
              register={register("purchaseDate", {
                required: {
                  value: true,
                  message: "Required Field",
                },
              })}
            />
            <TextInputLabelHookForm
              name="purchaseOrder"
              label="Purchase Order"
              type="text"
              required={true}
              className={
                errors.purchaseOrder && "border-2 border-red-500 col-span-2 "
              }
              register={register("purchaseOrder", {
                required: {
                  value: true,
                  message: "Required Field",
                },
              })}
            />
          </div>
          <div className="space-y-1 mr-6">
            <TextInputLabelHookForm
              name="quantityOrdered"
              label="Quantity Ordered"
              type="number"
              register={register("quantityOrdered", {})}
            />
            <TextInputLabelHookForm
              name="itemDescription"
              label="Item Description"
              type="text"
              required={true}
              className={
                errors.itemDescription && "border-2 border-red-500 col-span-2 "
              }
              register={register("itemDescription", {
                required: {
                  value: true,
                  message: "Required Field",
                },
                maxLength: {
                  value: 250,
                  message: "Maximum of 250 characters allowed.",
                },
              })}
              error={errors.itemDescription?.message}
            />
            <TextInputLabelHookForm
              name="modelNumber"
              label="Model Number"
              type="text"
              register={register("modelNumber", {})}
            />
            <TextInputLabelHookForm
              name="partNumber"
              label="Part Number"
              type="text"
              register={register("partNumber", {})}
            />
            <NumericInputLabel
              name="itemPrice"
              label="Item Price"
              register={register("itemPrice", {
                required: {
                  value: true,
                  message: "Required Field",
                },
              })}
              required={true}
              pattern="/^\[0-9]{6}\.?\[0-9{2}$/i,"
              type="number"
              step="0.01"
              min="0"
              max="999999.99"
              className={
                errors.itemPrice && "border-2 border-red-500 col-span-2 "
              }
            />
            {userPermissions.includes(deletePermission) && isEditState ? (
              <>
                {/* eslint-disable-next-line react/jsx-pascal-case */}
                <CheckBoxHookForm_2
                  name="isActive"
                  label="Status:"
                  type="checkbox"
                  validation={{
                    required: {
                      value: false,
                      message: "Required Field",
                    },
                  }}
                  hasPermission
                  disabled={false}
                  className={
                    errors.isActive && "border-2 border-red-500 col-span-2 "
                  }
                  register={register}
                />
              </>
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default OfficeApplianceFormDetails;
